import { nil, UIColor, UIRectangle, UITextView, UIView } from "uicore-ts"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHColor } from "../../Custom components/RH/RHColor"
import { formatRHDate } from "../../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHEuroFormatter } from "../../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHInvoiceEntry } from "../../SocketClientFunctions"
import { InvoiceLineRowView } from "./InvoiceLineRowView"


export class InvoiceReceiptView extends UIView {

    readonly titleLabel = new UITextView()
        .configuredWithObject({
            text: "Makse kinnitatud",
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: "20px",
                fontWeight: "700",
                lineHeight: "1.3"
            }
        })
        .addedAsSubviewToView(this)

    readonly stampLabel = new UITextView()
        .configuredWithObject({
            text: "TASUTUD",
            textColor: RHColor.success,
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: "15px",
                fontWeight: "700",
                letterSpacing: "3px",
                border: `2px solid ${RHColor.success.stringValue}`,
                borderRadius: "4px",
                padding: "4px 12px",
                display: "inline-block"
            }
        })
        .addedAsSubviewToView(this)

    readonly paidAtLabel = new UITextView()
        .configuredWithObject({
            textColor: RHColor.textSecondary,
            backgroundColor: UIColor.transparentColor,
            style: { fontSize: "13px" }
        })
        .addedAsSubviewToView(this)

    readonly separatorView = new UIView()
        .configuredWithObject({
            backgroundColor: RHColor.border
        })
        .addedAsSubviewToView(this)

    private _lineRows: InvoiceLineRowView[] = []

    readonly totalSeparatorView = new UIView()
        .configuredWithObject({
            backgroundColor: RHColor.success
        })
        .addedAsSubviewToView(this)

    readonly totalLabel = new UITextView()
        .configuredWithObject({
            text: "Kokku",
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: "15px",
                fontWeight: "700"
            }
        })
        .addedAsSubviewToView(this)

    readonly totalAmountLabel = new UITextView()
        .configuredWithObject({
            textColor: RHColor.success,
            backgroundColor: UIColor.transparentColor,
            textAlignment: UITextView.textAlignment.right,
            style: {
                fontSize: "15px",
                fontWeight: "700",
                fontVariantNumeric: "tabular-nums"
            }
        })
        .addedAsSubviewToView(this)

    readonly downloadButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Laadi PDF alla" },
            variant: "primary"
        })
        .addedAsSubviewToView(this)

    private _receiptHtml = ""

    configure(paidInvoices: RHInvoiceEntry[], receiptHtml: string) {
        this._receiptHtml = receiptHtml

        for (const row of this._lineRows) {
            row.removeFromSuperview()
        }
        this._lineRows = []

        for (const invoice of paidInvoices) {
            const row = new InvoiceLineRowView().addedAsSubviewToView(this) as InvoiceLineRowView
            const amount = RHEuroFormatter.formattedCurrency(invoice.amount, {
                currency: invoice.currency ?? "EUR",
                minimumFractionDigits: 2
            })
            row.configure(invoice.description, amount, invoice.status === "overdue")
            this._lineRows.push(row)
        }

        const total = paidInvoices.reduce((sum, inv) => sum + inv.amount, 0)
        this.totalAmountLabel.text = RHEuroFormatter.formattedCurrency(total, {
            currency: "EUR",
            minimumFractionDigits: 2
        })

        const paidAt = new Date()
        this.paidAtLabel.text = `Maksmise kuupäev: ${formatRHDate(paidAt)}`

        // noinspection JSConstantReassignment
        this.downloadButton.controlEventTargetAccumulator.PrimaryActionTriggered =
            () => this._downloadReceipt()
    }

    private _downloadReceipt() {
        const blob = new Blob([this._receiptHtml], { type: "text/html;charset=utf-8" })
        const url = URL.createObjectURL(blob)
        const anchor = document.createElement("a")
        anchor.href = url
        anchor.download = `arve-kinnitus-${new Date().toISOString().slice(0, 10)}.html`
        document.body.appendChild(anchor)
        anchor.click()
        document.body.removeChild(anchor)
        URL.revokeObjectURL(url)
    }

    override layoutSubviews() {
        super.layoutSubviews()
        const padding = this.core.paddingLength
        const bounds = this.contentBoundsWithInset(padding)
        const labelHeight = padding * 2
        const amountColumnWidth = padding * 8

        bounds
            .rectangleWithHeight(labelHeight * 1.5)
            .assignedAsFrameOfView(this.titleLabel)

            .rectangleForNextRow(padding * 0.5, labelHeight)
            .assignedAsFrameOfView(this.stampLabel)

            .rectangleForNextRow(padding * 0.5, labelHeight * 0.75)
            .assignedAsFrameOfView(this.paidAtLabel)

            .rectangleForNextRow(padding, 1)
            .assignedAsFrameOfView(this.separatorView)
            .ENDIF((frame: UIRectangle) => {
                let current = frame
                for (const row of this._lineRows) {
                    current = current
                        .rectangleForNextRow(0, row)
                        .assignedAsFrameOfView(row)
                }
                return current
            })

            .rectangleForNextRow(padding * 0.5, 2)
            .assignedAsFrameOfView(this.totalSeparatorView)

            .rectangleForNextRow(padding * 0.5, labelHeight)
            .distributeViewsAlongWidth(
                [this.totalLabel, this.totalAmountLabel],
                undefined,
                padding,
                [nil, amountColumnWidth]
            )

            .rectangleForNextRow(padding * 1.5, labelHeight * 1.5)
            .rectangleWithWidth(padding * 10, 0)
            .assignedAsFrameOfView(this.downloadButton)
    }

}
